import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Group,
  Indicator,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useMemo } from "react";
import {
  useLeadsContainerStore,
  useLeadsSheetStore,
} from "../../../../../stores/LeadsStore";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { IconBell, IconMessage } from "@tabler/icons-react";
import { getDifference } from "../../utils";
import { formatMinDate, parseDate } from "../../../../../functions/dates";
import MoreActions from "../actions/MoreActions";
import { useLocation, useNavigate } from "react-router-dom";
import { IconLinkedin } from "../../../../../components/Icons/IconLinkedin";
import { IconEmail } from "../../../../../assets/icons/IconEmail";
import { tagsColumn } from "../../../../../layouts/Table/columns/tagsColumn";
import { updateProspect } from "../../../../../api/PRMAPI";

const LeadItemMobile = ({ current, container, index }) => {
  const lang = useLanguage((s) => s.language);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem
  );
  const navigate = useNavigate();
  const location = useLocation();
  const handleShowDetail = (tab) => {
    const setOpened = useLeadsSheetStore.getState().setOpened;
    const setIndex = useLeadsSheetStore.getState().setIndex;
    const setCurrent = useLeadsSheetStore.getState().setCurrent;
    const setCurrentContainer =
      useLeadsSheetStore.getState().setCurrentContainer;
    const setActiveTab = useLeadsSheetStore.getState().setActiveTab;
    setOpened(true);
    setIndex(index);
    setCurrent(current);
    setCurrentContainer(container);
    if (tab) {
      setActiveTab(tab);
    }

    // Update URL so that on refresh, we reopen the lead sheet
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("contact", current.id);
    searchParams.set("container", container.id);
    searchParams.set("index", index);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  //#region About
  const about = useMemo(() => {
    const aboutList = [
      { key: "%last_name%", value: "lastName" },
      { key: "%first_name%", value: "firstName" },
      { key: "%job_title%", value: "jobTitle" },
      { key: "%company%", value: "company" },
      { key: "%phone%", value: "phone" },
      { key: "%contact_image_url%", value: "contact_image_url" },
    ];
    let result = {};

    aboutList.forEach((item) => {
      result[item.value] =
        current?.properties?.find((x) => x.identifier_placeholder === item.key)
          ?.value || " - ";
    });

    return result;
  }, [current?.properties]);

  const onTagEdited = (newTags) => {
    updateContainerItem(container.id, current.id, { tags: newTags });
  };
  //#endregion

  return (
    <Paper
      withBorder
      radius={"md"}
      h={"calc(100% - 14px)"}
      style={{
        // boxShadow: getChecked()
        //   ? `${
        //       container?.color ? `#${container?.color}66` : "var(--mantine-color-primary-3)"
        //     } 0px 1px 3px`
        //   : `#00000009 1px 2px 3px`,
        opacity: current?.new ? 0.3 : 1,
        borderColor: current?.new_reply
          ? "var(--mantine-color-secondary-6)"
          : "var(--mantine-color-primary-0)",
        borderWidth: current?.new_reply ? 3 : 1,
        cursor: "grab",
        position: "relative",
      }}
      bg={"white"}
      px={"sm"}
      py={8}
      draggable
      component={Group}
      pr={18}
    >
      <Avatar
        color="initials"
        src={about.contact_image_url}
        name={about.firstName + " " + about.lastName}
        imageProps={{
          referrerPolicy: "no-referrer",
        }}
        radius={"xl"}
        size={48}
        variant="filled"
      />
      <Stack gap={4} flex={"1 1 auto"} w={0}>
        <Group wrap="nowrap">
          <Tooltip label={about.firstName + " " + about.lastName}>
            <Anchor
              truncate
              fw={600}
              c={"#333"}
              fz={12}
              onClick={() => handleShowDetail("summary")}
            >
              {about.firstName} {about.lastName}
            </Anchor>
          </Tooltip>
          {current?.last_reply_date && (
            <Tooltip label={parseDate(current.last_reply_date, false)}>
              <Badge
                size="xs"
                color={"secondary.1"}
                bg={current?.new_reply ? undefined : "none"}
                c={"black"}
                fz={8}
                pt={2}
                miw={60}
                ml={"auto"}
                pos={"absolute"}
                bottom={0}
                right={0}
              >
                {formatMinDate(current.last_reply_date)}
              </Badge>
            </Tooltip>
          )}
        </Group>

        <Tooltip label={about.jobTitle} disabled={about.jobTitle === " - "}>
          <Text size="xs" truncate="end">
            {about.jobTitle}
          </Text>
        </Tooltip>

        <Tooltip label={about.company} disabled={about.company === " - "}>
          <Text size="xs" truncate="end">
            {about.company}
          </Text>
        </Tooltip>

        {/* {tagsColumn({
          editFunction: () => updateProspect,
          wrapperProps: { py: 4, px: 8, mt: 8 },
          onEdited: onTagEdited,
        }).renderCell({
          row: current,
          value: current.tags.map((e) => e.name).join(", "),
        })} */}
      </Stack>

      <Group gap={6}>
        <Indicator
          color="red"
          inline
          offset={2}
          disabled={current?.calls.length === 0}
          styles={{
            indicator: {
              width: 8,
              height: 8,
              minWidth: 0,
            },
          }}
          size={16}
        >
          <ActionIcon
            size={"md"}
            variant="subtle"
            color="red"
            className="lead-item-button"
            opacity={current?.calls.length === 0 ? undefined : 1}
            onClick={() => handleShowDetail("reminders")}
          >
            <IconBell size={18} />
          </ActionIcon>
        </Indicator>

        <Indicator
          color="red"
          inline
          offset={6}
          disabled={current?.number_of_replies === 0}
          styles={{
            indicator: {
              width: 8,
              height: 8,
              minWidth: 0,
            },
          }}
          size={16}
        >
          <ActionIcon
            size={"md"}
            variant="subtle"
            color="primary"
            className="lead-item-button"
            onClick={() => handleShowDetail("answers")}
          >
            {current?.number_of_replies === 0 ? (
              <IconMessage size={18} />
            ) : current.last_reply_type === "linkedin" ? (
              <IconLinkedin size={16} />
            ) : (
              <IconEmail size={16} />
            )}
          </ActionIcon>
        </Indicator>
      </Group>

      {current?.new_reply && (
        <Badge
          size="xs"
          color="secondary"
          fz={10}
          h={18}
          w={40}
          pos={"absolute"}
          top={-10}
          left={2}
        >
          new
        </Badge>
      )}

      <MoreActions current={current} container={container} />
    </Paper>
  );
};

export default LeadItemMobile;
